import { FinancialRecord } from '../types/data/financialRecord';
import axios from 'axios';

// API base URL configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

class FinancialRecordKPIService {
  // Create a single financial record with enriched KPIs
  async createFinancialRecord(record: Omit<FinancialRecord, 'id' | 'createdAt' | 'updatedAt'>): Promise<FinancialRecord> {
    try {
                  const response = await axios.post(`${API_BASE_URL}/financial-records-kpi`, record);
      
                  return response.data.data;
    } catch (error) {
      console.error('Error creating KPI financial record:', error);
      throw error;
    }
  }

  // Bulk create financial records with enriched KPIs
  async bulkCreateFinancialRecords(records: Omit<FinancialRecord, 'id' | 'createdAt' | 'updatedAt'>[]): Promise<{
    success: FinancialRecord[];
    errors: { record: FinancialRecord; error: string }[];
    duplicates: { record: FinancialRecord; existing: FinancialRecord }[];
  }> {
    try {
                  const response = await axios.post(`${API_BASE_URL}/financial-records-kpi/bulk`, { records });
      
                  return response.data.results;
    } catch (error) {
      console.error('Error bulk creating KPI financial records:', error);
      throw error;
    }
  }

  // Get all financial records with KPI structure
  async getFinancialRecords(filters?: {
    brands?: string[];
    dealers?: string[];
    years?: number[];
    months?: number[];
    kpiCodes?: string[];
    kpiCategories?: string[];
    owners?: string[];
    page?: number;
    pageSize?: number;
    isAnalyticsExport?: boolean;
  }): Promise<{
    records: FinancialRecord[];
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
  }> {
    try {
            // Build query parameters
      const params: Record<string, string | number> = {};
      
      if (filters) {
        if (filters.brands && filters.brands.length > 0) params.brands = filters.brands.join(',');
        if (filters.dealers && filters.dealers.length > 0) params.dealers = filters.dealers.join(',');
        if (filters.years && filters.years.length > 0) params.years = filters.years.join(',');
        if (filters.months && filters.months.length > 0) params.months = filters.months.join(',');
        if (filters.kpiCodes && filters.kpiCodes.length > 0) params.kpiCodes = filters.kpiCodes.join(',');
        if (filters.kpiCategories && filters.kpiCategories.length > 0) params.kpiCategories = filters.kpiCategories.join(',');
        if (filters.owners && filters.owners.length > 0) {
          params.owners = filters.owners.join(',');
                  }
        if (filters.page) params.page = filters.page;
        if (filters.pageSize) params.pageSize = filters.pageSize;
        if (filters.isAnalyticsExport) params.isAnalyticsExport = 'true';
      }
      
                        const response = await axios.get(`${API_BASE_URL}/financial-records-kpi`, { 
        params,
        headers: {
          ...(localStorage.getItem('token') && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
        }
      });
      
                        return response.data;
    } catch (error) {
      console.error('Error fetching KPI financial records:', error);
      if (axios.isAxiosError(error)) {
        console.error('DEBUG KPI SERVICE: Axios error details:', {
          message: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          url: error.config?.url,
          params: error.config?.params
        });
      }
      throw error;
    }
  }

  // Get filter options (optimized - dedicated endpoint)
  async getFilterOptions(appliedFilters?: {
    brands?: string[];
    dealers?: string[];
    years?: number[];
    months?: number[];
    kpiCategories?: string[];
    owners?: string[];
  }): Promise<{
    brands: string[];
    dealers: string[];
    years: number[];
    months: number[];
    kpiCategories: string[];
  }> {
    try {
      // Build query parameters for applied filters
      const params: Record<string, string> = {};
      
      if (appliedFilters) {
        if (appliedFilters.brands && appliedFilters.brands.length > 0) {
          params.brands = appliedFilters.brands.join(',');
        }
        if (appliedFilters.dealers && appliedFilters.dealers.length > 0) {
          params.dealers = appliedFilters.dealers.join(',');
        }
        if (appliedFilters.years && appliedFilters.years.length > 0) {
          params.years = appliedFilters.years.join(',');
        }
        if (appliedFilters.months && appliedFilters.months.length > 0) {
          params.months = appliedFilters.months.join(',');
        }
        if (appliedFilters.kpiCategories && appliedFilters.kpiCategories.length > 0) {
          params.kpiCategories = appliedFilters.kpiCategories.join(',');
        }
        if (appliedFilters.owners && appliedFilters.owners.length > 0) {
          params.owners = appliedFilters.owners.join(',');
        }
      }

      const response = await axios.get(`${API_BASE_URL}/financial-records-kpi/filter-options`, { 
        params,
        headers: {
          ...(localStorage.getItem('token') && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
        }
      });
      
      return response.data.data;
    } catch (error) {
      console.error('Error fetching filter options:', error);
      throw error;
    }
  }

  // Get a single financial record by ID
  async getFinancialRecordById(id: string): Promise<FinancialRecord | null> {
    try {
            const response = await axios.get(`${API_BASE_URL}/financial-records-kpi/${id}`);
      
            return response.data.data;
    } catch (error) {
      console.error(`Error fetching KPI financial record with ID ${id}:`, error);
      return null;
    }
  }

  // Update a financial record with enriched KPIs
  async updateFinancialRecord(id: string, updates: Partial<FinancialRecord>): Promise<FinancialRecord | null> {
    try {
                  const response = await axios.put(`${API_BASE_URL}/financial-records-kpi/${id}`, updates);
      
            return response.data.data;
    } catch (error) {
      console.error(`Error updating KPI financial record with ID ${id}:`, error);
      return null;
    }
  }

  // Delete a financial record
  async deleteFinancialRecord(id: string): Promise<boolean> {
    try {
            const response = await axios.delete(`${API_BASE_URL}/financial-records-kpi/${id}`);
      
            return true;
    } catch (error) {
      console.error(`Error deleting KPI financial record with ID ${id}:`, error);
      return false;
    }
  }

  // Delete all financial records
  async deleteAllFinancialRecords(): Promise<{ deletedCount: number }> {
    try {
            const response = await axios.delete(`${API_BASE_URL}/financial-records-kpi/all`);
      
                  return { deletedCount: response.data.deletedCount };
    } catch (error) {
      console.error('Error deleting all KPI financial records:', error);
      throw error;
    }
  }

  // Delete filtered financial records
  async deleteFilteredFinancialRecords(filters?: {
    brands?: string[];
    dealers?: string[];
    years?: number[];
    months?: number[];
    kpiCodes?: string[];
    kpiCategories?: string[];
    owners?: string[];
  }): Promise<{ deletedCount: number }> {
    try {
                  // Build query parameters
      const params: Record<string, string> = {};
      
      if (filters) {
        if (filters.brands && filters.brands.length > 0) params.brands = filters.brands.join(',');
        if (filters.dealers && filters.dealers.length > 0) params.dealers = filters.dealers.join(',');
        if (filters.years && filters.years.length > 0) params.years = filters.years.join(',');
        if (filters.months && filters.months.length > 0) params.months = filters.months.join(',');
        if (filters.kpiCodes && filters.kpiCodes.length > 0) params.kpiCodes = filters.kpiCodes.join(',');
        if (filters.kpiCategories && filters.kpiCategories.length > 0) params.kpiCategories = filters.kpiCategories.join(',');
        if (filters.owners && filters.owners.length > 0) {
          params.owners = filters.owners.join(',');
                  }
      }
      
            const response = await axios.delete(`${API_BASE_URL}/financial-records-kpi/filtered`, { params });
      
                  return { deletedCount: response.data.deletedCount };
    } catch (error) {
      console.error('Error deleting filtered KPI financial records:', error);
      throw error;
    }
  }

  // Get KPIs for a specific brand (optimized for KPI maintenance)
  async getBrandKPIs(brand: string): Promise<{
    brand: string;
    kpis: Array<{
      code: string;
      description: string;
      category: string;
      unit: string;
      recordCount: number;
      sampleValues: any[];
    }>;
  }> {
    try {
      console.log('🚀 PERFORMANCE: Using optimized brand KPIs API for brand:', brand);
      
      const response = await axios.get(`${API_BASE_URL}/financial-records-kpi/brands/${encodeURIComponent(brand)}/kpis`, {
        headers: {
          ...(localStorage.getItem('token') && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
        }
      });
      
      console.log('✅ PERFORMANCE: Brand KPIs loaded efficiently', {
        brand: response.data.data.brand,
        kpiCount: response.data.data.kpis.length,
        categories: Array.from(new Set(response.data.data.kpis.map((kpi: any) => kpi.category)))
      });
      
      return response.data.data;
    } catch (error) {
      console.error('Error fetching brand KPIs:', error);
      throw error;
    }
  }
}

export default new FinancialRecordKPIService(); 